import { apiEndpoint } from "../utils/env";

async function fetchWithAuth(url, token, options = {}) {
  const headers = options.headers || {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  headers["Content-Type"] = headers["Content-Type"] || "application/json";
  const res = await fetch(url, { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }
  if (!res.ok) {
    const errMsg =
      json && json.message
        ? json.message
        : typeof json === "string"
        ? json
        : res.statusText;
    const error = new Error(errMsg || `Request failed: ${res.status}`);
    error.code = json && json.code ? json.code : null;
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

// Host Management APIs
export async function getHosts(token) {
  const url = apiEndpoint("/api/Admin/hosts");
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function getHost(token, hostId) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

export async function addHost(token, hostData) {
  const url = apiEndpoint("/api/Admin/hosts");
  return fetchWithAuth(url, token, {
    method: "POST",
    body: JSON.stringify(hostData),
  });
}

export async function updateHost(token, hostId, hostData) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}`);
  return fetchWithAuth(url, token, {
    method: "PUT",
    body: JSON.stringify(hostData),
  });
}

export async function deleteHost(token, hostId) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}`);
  return fetchWithAuth(url, token, { method: "DELETE" });
}

export async function testHost(token, hostId) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}/test`);
  return fetchWithAuth(url, token, { method: "POST" });
}

// The configuration that would be sent to this host, with every secret masked. The real values stay on
// the server — the browser never needs them, and the install re-derives the file server-side.
export async function getJoinPackagePreview(token, hostId, { nodeName, installDir, dataDir, kind } = {}) {
  const params = new URLSearchParams();
  if (nodeName) params.set("nodeName", nodeName);
  if (installDir) params.set("installDir", installDir);
  if (dataDir) params.set("dataDir", dataDir);
  // "ingest" limits the target to the worker and the gatherer; anything else is a
  // full second node, which is also what the server assumes when this is absent.
  if (kind) params.set("kind", kind);
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}/provision/preview?${params.toString()}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Install Depository on a bare host by running the real installer there over SSH. Returns as soon as
// the job starts — a full install moves hundreds of megabytes, so progress is polled below.
// Pass no `conf` for the normal path: the server builds the configuration from its own settings.
export async function provisionHost(token, hostId, options = {}) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}/provision`);
  return fetchWithAuth(url, token, { method: "POST", body: JSON.stringify(options) });
}

// `since` is the line index the caller already has, so polling does not re-send the whole log.
export async function getProvisionStatus(token, hostId, since = 0) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}/provision/status?since=${since}`);
  return fetchWithAuth(url, token, { method: "GET" });
}

// Point the host at the directory the installer just used, so deploying a worker there needs no retyping.
export async function adoptProvisionedInstall(token, hostId) {
  const url = apiEndpoint(`/api/Admin/hosts/${hostId}/provision/adopt`);
  return fetchWithAuth(url, token, { method: "POST" });
}

export async function getHostStatistics(token) {
  const url = apiEndpoint("/api/Admin/hosts/statistics");
  return fetchWithAuth(url, token, { method: "GET" });
}
